// src/components/ImageUploader.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { logUserUpload } from '../utils/supabaseUtils';

const removeBgApiKey = import.meta.env.VITE_REMOVE_BG_API_KEY;

console.log('All env variables:', JSON.stringify(import.meta.env, null, 2));

const ImageUploader = ({ onUploadComplete }) => {
  const [uploadedImageURL, setUploadedImageURL] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      console.log('ImageUploader session check:', data, error);
      if (error || !data.session) {
        console.log('No session found, redirecting to login');
        navigate('/login');
      } else {
        setUser(data.session.user);
      }
      setCheckingSession(false);
    };
    fetchSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event in ImageUploader:', event, session);
      if (event === 'SIGNED_IN' && session) {
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        navigate('/login');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const handleFileChange = async (event) => {
    if (!user) {
      setError('Please log in to upload an image');
      return;
    }

    setIsLoading(true);
    setError(null);
    const file = event.target.files[0];
    if (!file) {
      setIsLoading(false);
      return;
    }

    try {
      console.log('Remove.bg API Key:', removeBgApiKey);
      if (!removeBgApiKey) throw new Error('Remove.bg API key is missing');

      // Create FormData with high quality parameters for large format printing
      const formData = new FormData();
      formData.append('image_file', file);
      formData.append('size', 'full');         // Request full resolution output
      formData.append('format', 'png');        // PNG format for highest quality
      formData.append('channels', 'rgba');     // Include alpha channel for transparency
      formData.append('type', 'auto');         // Auto-detect image type
      formData.append('bg_color', '');         // Empty string for transparent background
      formData.append('scale', '100%');        // No downscaling
      formData.append('crop', 'false');        // Don't crop the image
      formData.append('position', 'center');   // Center the subject
      formData.append('roi', '0% 0% 100% 100%'); // Use full image as region of interest
      formData.append('add_shadow', 'false');  // No shadow for clean compositing
      formData.append('semitransparency', 'true'); // Keep semi-transparent pixels for better edges

      console.log('Processing image for large format printing:', file.name);
      console.log('Image size:', Math.round(file.size / 1024), 'KB');
      console.log('Image type:', file.type);

      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: {
          'X-API-Key': removeBgApiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Remove.bg response:', response.status, errorText);
        throw new Error(`Remove.bg API error: ${response.status} - ${errorText}`);
      }

      // Log response headers for debugging quality issues
      const headers = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      console.log('Remove.bg response headers:', headers);

      const blob = await response.blob();
      console.log('Processed image size:', Math.round(blob.size / 1024), 'KB');
      
      const processedFile = new File([blob], `processed-hq-${Date.now()}.png`, { type: 'image/png' });

      const filePath = `background_removed_images/${user.id}/${processedFile.name}`;
      const { data, error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, processedFile, { upsert: true });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }

      const { publicUrl } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath).data;

      console.log('Public URL:', publicUrl);
      await logUserUpload(user.id, publicUrl);

      setUploadedImageURL(publicUrl);
      onUploadComplete(publicUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingSession) {
    return <div>Checking session...</div>;
  }

  return (
    <div>
      <input type="file" accept="image/*" onChange={handleFileChange} disabled={isLoading || !user} />
      {isLoading && <p>Processing image...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {uploadedImageURL && <img src={uploadedImageURL} alt="Processed Image" style={{ maxWidth: '300px' }} />}
    </div>
  );
};

export default ImageUploader;